import { useEffect, useState } from 'react';
import {
	Box,
	Table,
	TableRow,
	TableBody,
	TableCell,
	TableContainer,
	TablePagination,
	TableHead,
	TableFooter,
	TableSortLabel,
	TextField
} from '@mui/material';

import Label from '../../../../Components/Label';
import Loader from '../../../../Components/Loader';
import useStyles from '../../styles';
import RenderRowCells from './RenderRowCells';

const DEFAULT_SEARCH_KEYS = ['siteDomain', 'siteId'];

const CustomTableHeader = ({ header, orderBy, order, onRequestSort, getSortLabel, tableHeadClassName, classes }) => (
	<TableHead className={tableHeadClassName || classes.customTableHead}>
		<TableRow>
			{header.map((col) => {
				return (
					<TableCell
						key={col.id}
						sx={col.sx}
						sortDirection={col.sortable && orderBy === col.id ? order : false}
					>
						{col.sortable && onRequestSort ? (
							<TableSortLabel
								active={orderBy === col.id}
								direction={orderBy === col.id ? order : 'asc'}
								onClick={() => onRequestSort(col.id)}
								aria-label={getSortLabel(col.id)}
								title={getSortLabel(col.id)}
							>
								<b>{col.label}</b>
							</TableSortLabel>
						) : (
							<b>{col.label}</b>
						)}
					</TableCell>
				);
			})}
		</TableRow>
	</TableHead>
);

const CustomTableBody = ({
	filteredRows,
	page,
	rowsPerPage,
	rowIdKey,
	handleRowClick,
	renderRowCells
}) => (
	<TableBody>
		{filteredRows && filteredRows.length
			? filteredRows
					.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
					.map((row) => {
						const rowId = row[rowIdKey];

						if (renderRowCells) {
							return (
								<TableRow hover onClick={() => handleRowClick(rowId)} key={rowId}>
									<RenderRowCells row={row} rowId={rowId} renderRowCells={renderRowCells} />
								</TableRow>
							);
						}

						const { siteId, ownerEmail, siteDomain, tag } = row;
						return (
							<TableRow hover onClick={() => handleRowClick(siteId)} key={siteId}>
								<TableCell>{siteId}</TableCell>
								<TableCell>{siteDomain}</TableCell>
								<TableCell>{ownerEmail}</TableCell>
								{tag && (
									<TableCell>
										<Label variant="ghost" color="success">
											{tag}
										</Label>
									</TableCell>
								)}
							</TableRow>
						);
					})
			: null}
	</TableBody>
);

// TODO: this needs a cleanup and refactor. It is a bit messy and has hard coded values.
export default function CustomTable(props) {
	const classes = useStyles();
	const {
		sites,
		rows,
		header,
		rowsPerPageInit,
		rowsPerPageOptions,
		sitesLoaded,
		rowsLoaded,
		handleRowClick,
		renderRowCells,
		rowIdKey = 'siteId',
		searchLabel = 'Search Site By Domain or SiteId',
		searchKeys = DEFAULT_SEARCH_KEYS,
		tableContainerClassName,
		tableHeadClassName,
		searchClassName,
		searchStyles,
		searchPlaceholder,
		searchTextFieldProps = {},
		searchAdornment = null,
		resetSearchOnRowsChange = true,
		orderBy,
		order,
		onRequestSort
	} = props;
	const { InputLabelProps, ...restSearchTextFieldProps } = searchTextFieldProps;
	const tableRows = rows || sites || [];
	const isLoaded = typeof rowsLoaded === 'boolean' ? rowsLoaded : sitesLoaded;
	const resolvedSearchClassName = searchClassName ?? (!searchAdornment ? classes.customTableSearch : undefined);
	const getSortLabel = (columnId) => {
		if (orderBy !== columnId) {
			return 'Click to sort';
		}

		return order === 'asc'
			? 'Sorted ascending, click to sort descending'
			: 'Sorted descending, click to sort ascending';
	};

	// Component State
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageInit);
	const [filteredRows, setFilteredRows] = useState(tableRows);
	const [searched, setSearched] = useState('');

	const handlePageChange = (e, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (e) => {
		setRowsPerPage(parseInt(e.target.value, 10));
		setPage(0);
	};

	const handleSearch = (e) => {
		const searchVal = e.target.value;
		setSearched(searchVal);
	};

	useEffect(() => {
		if (tableRows) {
			setFilteredRows(tableRows);
			if (resetSearchOnRowsChange) {
				setSearched('');
			}
		}
	}, [resetSearchOnRowsChange, tableRows]);

	useEffect(() => {
		const searchedChanged = () => {
			const filteredData = !!tableRows.length
				? tableRows.filter((row) => {
					if (!searched) {
						return row;
					}

					return searchKeys.some((key) => {
						const value = row?.[key];
						return typeof value !== 'undefined' && String(value).toLowerCase().includes(searched.toLowerCase());
					});
				})
				: [];

			setFilteredRows(filteredData);
			setPage(0);
		};
		searchedChanged();
	}, [searched, tableRows, searchKeys]);

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'flex-start',
					gap: 1.5,
					flexWrap: 'wrap',
					mb: 0
				}}
			>
				<TextField
					fullWidth
					label={searchLabel}
					placeholder={searchPlaceholder}
					value={searched}
					onChange={handleSearch}
					className={resolvedSearchClassName}
					sx={{ flex: '1 1 320px', ...searchStyles }}
					InputLabelProps={InputLabelProps}
					{...restSearchTextFieldProps}
				/>
				{searchAdornment}
			</Box>
			{isLoaded && (
				<TableContainer className={tableContainerClassName || classes.customTableContainer}>
					<Table>
						<CustomTableHeader
							header={header}
							orderBy={orderBy}
							order={order}
							onRequestSort={onRequestSort}
							getSortLabel={getSortLabel}
							tableHeadClassName={tableHeadClassName}
							classes={classes}
						/>
						<CustomTableBody
							filteredRows={filteredRows}
							page={page}
							rowsPerPage={rowsPerPage}
							rowIdKey={rowIdKey}
							handleRowClick={handleRowClick}
							renderRowCells={renderRowCells}
						/>
						<TableFooter>
							<TableRow>
								<TablePagination
									rowsPerPageOptions={rowsPerPageOptions}
									count={filteredRows.length}
									rowsPerPage={rowsPerPage}
									page={page}
									onPageChange={handlePageChange}
									onRowsPerPageChange={handleChangeRowsPerPage}
								/>
							</TableRow>
						</TableFooter>
					</Table>
				</TableContainer>
			)}

			{!isLoaded && <Loader />}
		</>
	);
}
